import { useEffect, useState } from "react";
import { BookOpen, Check, Info } from "lucide-react";
import { getFellowships, type FellowshipId, type FellowshipOption } from "@/lib/api";

/**
 * "Which literature do you use?" (Fellowships PRD §4, §10.1).
 *
 * The question is deliberately about LITERATURE, not identity — nobody is asked
 * to declare a fellowship. Optional, skippable, multi-select, changeable, and
 * never a gate. Selecting nothing is a first-class answer: the step readings
 * then say to open your own book, and the app cites nothing rather than
 * guessing (and never falls back to AA).
 *
 * Fellowship names appear descriptively so a person can recognise their own
 * books. No logos, no marks, nothing implying endorsement (§8).
 */

/** What the picker reports back about the word this selection implies. */
export type PreviewTerm = { adj: string; card: string; date: string; neutral: boolean };

/** Mirrors the Worker's termFor: agreeing words win, disagreement goes neutral. */
export function previewTerm(
  selected: FellowshipId[],
  options: FellowshipOption[]
): PreviewTerm {
  const words = new Set(
    selected.map((id) => options.find((o) => o.id === id)?.term.adj).filter(Boolean)
  );
  if (words.size !== 1) {
    return {
      adj: "in recovery",
      card: "Recovery Time",
      date: "Recovery start date",
      neutral: true,
    };
  }
  const first = options.find((o) => o.id === selected[0])!;
  return {
    adj: first.term.adj,
    card: `${first.term.noun} Time`,
    date: first.term.date,
    neutral: false,
  };
}

export function LiteraturePicker({
  value,
  onChange,
}: {
  value: FellowshipId[];
  /** Reports the new selection and the word it implies (see PreviewTerm). */
  onChange: (next: FellowshipId[], term: PreviewTerm) => void;
}) {
  const [options, setOptions] = useState<FellowshipOption[]>([]);

  useEffect(() => {
    getFellowships()
      .then(setOptions)
      .catch(() => {
        /* The picker simply doesn't render — never blocks the screen it's on. */
      });
  }, []);

  const t = previewTerm(value, options);
  const toggle = (id: FellowshipId) => {
    const next = value.includes(id) ? value.filter((x) => x !== id) : [...value, id];
    onChange(next, previewTerm(next, options));
  };

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-col gap-2.5">
        {options.map((o) => {
          const on = value.includes(o.id);
          return (
            <button
              key={o.id}
              type="button"
              aria-pressed={on}
              onClick={() => toggle(o.id)}
              className={`flex min-h-[44px] items-start gap-3 rounded-lg border px-4 py-3.5 text-left transition-colors ${
                on ? "border-2 border-primary bg-primary/[0.06]" : "border-border"
              }`}
            >
              <span
                className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-[5px] ${
                  on
                    ? "bg-primary text-primary-foreground"
                    : "border-[1.5px] border-border bg-transparent"
                }`}
              >
                {on && <Check className="h-[13px] w-[13px]" strokeWidth={2.5} />}
              </span>
              <span className="min-w-0 flex-1">
                <span className="block text-[15px] font-semibold text-foreground">{o.name}</span>
                <span className="mt-0.5 block text-[12.5px] leading-relaxed text-muted-foreground">
                  {o.books}
                </span>
              </span>
            </button>
          );
        })}
      </div>

      <div className="flex items-start gap-3 rounded-lg border border-border bg-secondary px-4 py-3">
        <BookOpen className="mt-0.5 h-4 w-4 shrink-0 text-primary" strokeWidth={2} />
        <p className="text-[12.5px] leading-relaxed text-muted-foreground">
          If you work the steps outside any fellowship — on your own, with a therapist, or from a
          book of your own — leave this blank. The readings will simply say to open your own book.
        </p>
      </div>

      {value.length > 0 && (
        <p className="flex items-start gap-1.5 text-[12.5px] leading-relaxed text-muted-foreground">
          <Info className="mt-0.5 h-3 w-3 shrink-0" strokeWidth={2} />
          <span>
            The app will say <strong className="font-semibold text-foreground">{t.adj}</strong>
            {t.neutral
              ? " — your selections use different words, so nothing is put in your mouth"
              : ""}
            , and your milestone reads{" "}
            <strong className="font-semibold text-foreground">{t.card}</strong>.
          </span>
        </p>
      )}
    </div>
  );
}
